import {
  Button,
  CopyToClipboard,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { useContext, useState } from "react";
import { OrderIdContext, StageContext } from "../../contexes/OrderIdContext";
import { useExecuteRedrive } from "../redrive/hooks/useExecuteRedrive";
import { AsyncActionButton, AsyncStatus } from "./AsyncActionButton";

interface ActionButtonsProps {
  modalHandler: () => void;
  toggleAllSections: (expand: boolean) => void;
  inititalExpandedState?: boolean;
  copyConfig?: {
    textToCopy: string;
    buttonText: string;
    successText: string;
    errorText: string;
  };
  herdLink?: string;
  showRedrive: boolean;
}

export function ActionButtons({
  modalHandler,
  toggleAllSections,
  inititalExpandedState,
  copyConfig,
  herdLink,
  showRedrive,
}: ActionButtonsProps) {
  const orderId = useContext<string>(OrderIdContext);
  const stage = useContext(StageContext);

  const [isExpanded, setIsExpanded] = useState(inititalExpandedState);
  const [redriveStatus, setRedriveStatus] = useState(AsyncStatus.NOT_STARTED);
  const [redriveError, setRedriveError] = useState<string>();

  const { executeSingleRedrive } = useExecuteRedrive();

  const handleRedriveOrder = async () => {
    if (!orderId || !stage) {
      setRedriveError("Missing order ID or stage");
      setRedriveStatus(AsyncStatus.FAILED);
      return;
    }

    setRedriveStatus(AsyncStatus.LOADING);

    try {
      const { data } = await executeSingleRedrive(orderId, stage);
      if (data?.executionId) {
        setRedriveStatus(AsyncStatus.SUCCESSFUL);
      } else {
        setRedriveError("No execution ID returned");
        setRedriveStatus(AsyncStatus.FAILED);
      }
    } catch (e) {
      const axiosError = e as { response?: { data?: { message?: string } } };
      const message =
        axiosError.response?.data?.message ||
        (e instanceof Error ? e.message : "Redrive failed");
      setRedriveError(message);
      setRedriveStatus(AsyncStatus.FAILED);
    }
  };

  const handleToggle = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    toggleAllSections(newExpandedState);
  };

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button onClick={modalHandler} iconName="command-prompt">
        View JSON
      </Button>
      <Button
        onClick={handleToggle}
        iconName={isExpanded ? "zoom-out" : "zoom-in"}
      >
        {isExpanded ? "Collapse All" : "Expand All"}
      </Button>
      {herdLink && (
        <Button href={herdLink} target="_blank" iconName="external">
          View Herd
        </Button>
      )}
      {copyConfig && (
        <CopyToClipboard
          textToCopy={copyConfig.textToCopy}
          copyButtonText={copyConfig.buttonText}
          copySuccessText={copyConfig.successText}
          copyErrorText={copyConfig.errorText}
        />
      )}
      {showRedrive && (
        <AsyncActionButton
          label="Redrive Order"
          successLabel="Redrive Successful"
          failedLabel="Redrive Failed"
          status={redriveStatus}
          onClick={handleRedriveOrder}
          errorMessage={redriveError}
        />
      )}
    </SpaceBetween>
  );
}

ActionButtons.defaultProps = {
  inititalExpandedState: true,
};
